import { draftsToSendMessageItems } from "./draftsToSendMessageItems.util";

import { draftWaitsForAcceptance, requestWaitsForAcceptance } from "./pendingInvitation.util";

import { resolveSendThreadId } from "./resolveSendThreadId.util";

import type { TFunction } from "i18next";

import type { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import type { SendMessagesRequest } from "@/pages/Private/redux/schedule/schedule.api";

import type { Draft } from "./inboxDraft.types";

interface ComposerSendRequestParams {
	lead: {
		id: number;
		campaignId?: Parameters<typeof resolveSendThreadId>[1];
		prospect?: { conversations?: Conversation[] | null } | null;
	};
	sendableDrafts: Draft[];
	signatureDefault?: boolean;
	pendingInvitations: Map<number, string | null>;
}

export interface ComposerSendRequest {
	request: SendMessagesRequest;
	/** The request parks its LinkedIn messages behind the sender's pending invitation. */
	waitsForAcceptance: boolean;
	/** Days after the acceptance the first parked message goes out (0: at once); null: none waits. */
	acceptanceDelayDays: number | null;
}

/**
 * The composer's send request (extracted from `useAddInboxChat`): the lead, the email thread the
 * reply belongs to, the messages, and — "Send when accepted" (PREC-4216) — the flag that parks
 * the messages of a sender whose invitation to the lead is still pending behind it. A draft from
 * a connected sender sends no flag, so the API treats it as it always did.
 */
export const buildComposerSendRequest = ({
	lead,
	sendableDrafts,
	signatureDefault,
	pendingInvitations,
}: ComposerSendRequestParams): ComposerSendRequest => {
	const threadId = resolveSendThreadId(lead.prospect?.conversations ?? [], lead.campaignId);
	const waitsForAcceptance = requestWaitsForAcceptance(sendableDrafts, pendingInvitations);
	const firstParked = sendableDrafts.find((draft) =>
		draftWaitsForAcceptance(draft, pendingInvitations)
	);

	return {
		request: {
			listPersonId: lead.id,
			...(threadId ? { threadId } : {}),
			messages: draftsToSendMessageItems(sendableDrafts, signatureDefault, (draft) =>
				draftWaitsForAcceptance(draft, pendingInvitations)
			),
			...(waitsForAcceptance ? { waitForInviteAcceptance: true } : {}),
		},
		waitsForAcceptance,
		acceptanceDelayDays: firstParked ? (firstParked.timing.scheduledInDays ?? 0) : null,
	};
};

/**
 * What the snackbar says after a send: sent, or scheduled behind the invitation — "as soon as it is
 * accepted" only when the first parked message has no delay, otherwise the delay it was given.
 */
export const composerSentMessage = (
	count: number,
	acceptanceDelayDays: number | null,
	t: TFunction
): string => {
	if (acceptanceDelayDays !== null) {
		return acceptanceDelayDays > 0
			? t("inbox.pendingInvitation.scheduledAfterDays", { count: acceptanceDelayDays })
			: t("inbox.pendingInvitation.scheduled");
	}

	return count > 1
		? `${count} messages sent successfully. Click to view in Outbox`
		: "Message sent successfully. Click to view in Outbox";
};

/** The Undo toast's first line: the reply was sent, or it waits for the acceptance (PREC-4216). */
export const undoToastHeadline = (acceptanceDelayDays: number | null, t: TFunction): string => {
	if (acceptanceDelayDays === null) {
		return t("inbox.undoSend.replySent");
	}

	return acceptanceDelayDays > 0
		? t("inbox.pendingInvitation.undoScheduledAfterDays", { count: acceptanceDelayDays })
		: t("inbox.pendingInvitation.undoScheduled");
};
